import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../axios";
import printLayoutApi from "../../api/printLayoutApi";
import InvoiceTemplate from "../../components/InvoiceTemplate";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  ViewColumn as ViewColumnIcon,
  TextFields as TextFieldIcon,
  Code as CodeIcon,
  RestartAlt as ResetIcon,
  DragIndicator as DragHandleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Preview as PreviewIcon,
  Image as ImageIcon
} from "@mui/icons-material";
import { toast } from "react-toastify";

// Mock Data for Live Preview
const mockData = {
  invoiceNumber: "INV-2025-001",
  issueDate: new Date().toISOString(),
  invoiceTitle: "فاتورة ضريبية",
  referenceNumber: "REF-12345",
  salesmanName: "محمد أحمد",
  company: {
    name: "شركة أرباح التقنية",
    address: "الرياض - شارع العليا",
    phone: "0500000000",
    email: "info@tech-solutions.com",
    taxNumber: "300000000000003",
    website: "www.tech-solutions.com"
  },
  customer: {
    name: "شركة الأفق للتجارة",
    mobile: "0555555555",
    address: "جدة - حي الروضة",
    taxNumber: "311111111111113"
  },
  items: [
    { 
      arabicName: "لابتوب ديل XPS 15", 
      unitName: "حبة", 
      quantity: 1, 
      unitPrice: 5000, 
      discount: 200, 
      taxValue: 720,
      description: "معالج i7، رام 16 جيجا"
    },
    { 
      arabicName: "ماوس لوجيتك MX Master", 
      unitName: "حبة", 
      quantity: 2, 
      unitPrice: 400, 
      discount: 0, 
      taxValue: 120,
      description: "لاسلكي، لون أسود"
    },
    { 
      arabicName: "اشتراك صيانة سنوي", 
      unitName: "سنة", 
      quantity: 1, 
      unitPrice: 1500, 
      discount: 0, 
      taxValue: 225,
      description: "دعم فني 24/7"
    }
  ],
  totals: {
    subtotal: 7300,
    discountTotal: 200,
    totalVAT: 1065,
    net: 8165
  },
  qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=InvoiceDemo"
};

const defaultColumns = [
  { key: "index", labelAr: "#", labelEn: "#", width: "5%", visible: true, order: 1 },
  { key: "item", labelAr: "الصنف", labelEn: "Item", width: "30%", visible: true, order: 2 },
  { key: "unit", labelAr: "الوحدة", labelEn: "Unit", width: "10%", visible: true, order: 3 },
  { key: "quantity", labelAr: "الكمية", labelEn: "Qty", width: "10%", visible: true, order: 4 },
  { key: "price", labelAr: "السعر", labelEn: "Price", width: "10%", visible: true, order: 5 },
  { key: "total", labelAr: "المجموع", labelEn: "Total", width: "10%", visible: true, order: 6 },
  { key: "discount", labelAr: "الخصم", labelEn: "Discount", width: "10%", visible: true, order: 7 },
  { key: "tax", labelAr: "الضريبة", labelEn: "Tax", width: "10%", visible: true, order: 8 },
  { key: "net", labelAr: "الصافي", labelEn: "Net", width: "15%", visible: true, order: 9 },
  { key: "description", labelAr: "الوصف", labelEn: "Description", width: "100%", visible: false, order: 10 },
];

const availableVariables = [
  { key: "{CompanyName}", label: "اسم الشركة" },
  { key: "{CompanyAddress}", label: "عنوان الشركة" },
  { key: "{CompanyPhone}", label: "هاتف الشركة" },
  { key: "{CompanyEmail}", label: "بريد الشركة" },
  { key: "{CompanyTax}", label: "الرقم الضريبي للشركة" },
  { key: "{InvoiceNumber}", label: "رقم الفاتورة" },
  { key: "{IssueDate}", label: "تاريخ الفاتورة" },
  { key: "{ReferenceNumber}", label: "الرقم المرجعي" },
  { key: "{CustomerName}", label: "اسم العميل" },
  { key: "{CustomerMobile}", label: "جوال العميل" },
  { key: "{CustomerAddress}", label: "عنوان العميل" },
  { key: "{CustomerTax}", label: "الرقم الضريبي للعميل" },
  { key: "{TotalBeforeTax}", label: "الإجمالي قبل الضريبة" },
  { key: "{TotalTax}", label: "إجمالي الضريبة" },
  { key: "{TotalDiscount}", label: "إجمالي الخصم" },
  { key: "{NetTotal}", label: "صافي الفاتورة" },
  { key: "{User}", label: "المستخدم" },
];

export default function PrintLayoutEditor() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [tabValue, setTabValue] = useState(0);
  
  const [layout, setLayout] = useState({
    headerHtml: "",
    footerHtml: "",
    customCss: "",
    showCompanyHeader: true,
    columns: defaultColumns,
    paperSize: "A4",
  });

  const [draggedColIndex, setDraggedColIndex] = useState(null);

  useEffect(() => {
    if (companyId) {
      axios.get(`/InvoiceTypes?companyId=${companyId}`).then(res => {
        setInvoiceTypes(res.data);
        if (res.data.length > 0) setSelectedType(res.data[0].invoiceTypeId);
      });
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && selectedType) {
      loadLayout();
    }
  }, [companyId, selectedType]);

  const loadLayout = async () => {
    try {
      const res = await printLayoutApi.get(companyId, selectedType);
      if (res.data && res.data.layoutJson) {
        const savedLayout = JSON.parse(res.data.layoutJson);
        const mergedColumns = defaultColumns.map(defCol => {
          const savedCol = savedLayout.columns?.find(c => c.key === defCol.key);
          return savedCol ? { ...defCol, ...savedCol } : defCol;
        });
        mergedColumns.sort((a, b) => (a.order || 99) - (b.order || 99));
        setLayout({ ...savedLayout, columns: mergedColumns });
      } else {
        setLayout({
          headerHtml: "",
          footerHtml: "",
          customCss: "",
          showCompanyHeader: true,
          columns: defaultColumns,
          paperSize: "A4",
        });
      }
    } catch (err) {
      console.error("Failed to load layout", err);
    }
  };

  const handleSave = async () => {
    try {
      if (!selectedType) {
        toast.error("يرجى اختيار نوع الفاتورة أولاً");
        return;
      }
      await printLayoutApi.save({
        companyId,
        invoiceTypeId: selectedType,
        layoutJson: JSON.stringify(layout),
      });
      toast.success("تم حفظ تصميم الطباعة بنجاح");
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.response?.data?.message || "فشل حفظ التصميم");
    }
  };

  const handleColumnChange = (index, field, value) => {
    const newColumns = [...layout.columns];
    newColumns[index] = { ...newColumns[index], [field]: value };
    setLayout({ ...layout, columns: newColumns });
  };

  const handleColumnDragStart = (e, index) => {
    setDraggedColIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleColumnDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleColumnDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedColIndex === null || draggedColIndex === dropIndex) return;

    const newCols = [...layout.columns];
    const draggedItem = newCols[draggedColIndex];
    newCols.splice(draggedColIndex, 1);
    newCols.splice(dropIndex, 0, draggedItem);

    const reordered = newCols.map((col, idx) => ({ ...col, order: idx + 1 }));
    setLayout({ ...layout, columns: reordered });
    setDraggedColIndex(null);
  };

  const handleImageUpload = (event, targetField) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) { // 1MB limit
      toast.error("حجم الصورة كبير جداً. الحد الأقصى هو 1 ميجابايت.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const imgTag = `<img src="${reader.result}" style="max-width: 100%; height: auto;" />`;
      setLayout(prev => ({
        ...prev,
        [targetField]: (prev[targetField] || "") + imgTag
      }));
      toast.success("تم إدراج الصورة بنجاح");
    };
    reader.readAsDataURL(file);
  };

  const insertVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.info(`تم نسخ المتغير ${variable}، يمكنك لصقه في المحرر`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }} dir="rtl">
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
            <CodeIcon color="primary" /> محرر تصميم الطباعة المرن
          </Typography>
          <Box display="flex" gap={2}>
            <Button variant="outlined" color="warning" onClick={() => loadLayout()} startIcon={<ResetIcon />}>
              إعادة تعيين
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} startIcon={<SaveIcon />}>
              حفظ التصميم
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Select
              fullWidth
              size="small"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>اختر نوع الفاتورة</MenuItem>
              {invoiceTypes.map(t => (
                <MenuItem key={t.invoiceTypeId} value={t.invoiceTypeId}>{t.name}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <Select
              fullWidth
              size="small"
              value={layout.paperSize}
              onChange={(e) => setLayout({ ...layout, paperSize: e.target.value })}
            >
              <MenuItem value="A4">A4 (قياسي)</MenuItem>
              <MenuItem value="A5">A5 (نصفي)</MenuItem>
              <MenuItem value="80mm">80mm (حراري/كاشير)</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={layout.showCompanyHeader}
                  onChange={(e) => setLayout({ ...layout, showCompanyHeader: e.target.checked })}
                />
              }
              label="إظهار ترويسة الشركة الافتراضية"
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column: Editor */}
        <Grid item xs={12} lg={5}>
          <Paper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box borderBottom={1} borderColor="divider">
              <Tabs 
                value={tabValue} 
                onChange={(e, v) => setTabValue(v)} 
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab icon={<TextFieldIcon />} label="الرأس" iconPosition="start" />
                <Tab icon={<ViewColumnIcon />} label="الأعمدة" iconPosition="start" />
                <Tab icon={<TextFieldIcon />} label="الذيل" iconPosition="start" />
                <Tab icon={<CodeIcon />} label="CSS" iconPosition="start" />
              </Tabs>
            </Box>

            <Box p={2} sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
              {/* Header Editor */}
              {tabValue === 0 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold">المتغيرات المتاحة (اضغط للنسخ):</Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<ImageIcon />}
                    >
                      إدراج صورة
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'headerHtml')}
                      />
                    </Button>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                    {availableVariables.map((v) => (
                      <Chip 
                        key={v.key} 
                        label={`${v.label} ${v.key}`} 
                        onClick={() => insertVariable(v.key)} 
                        size="small" 
                        variant="outlined" 
                        clickable
                        icon={<CopyIcon />}
                      />
                    ))}
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    value={layout.headerHtml}
                    onChange={(e) => setLayout({ ...layout, headerHtml: e.target.value })}
                    placeholder="<div>...</div>"
                    helperText="أدخل كود HTML للرأس هنا. استخدم المتغيرات أعلاه لإدراج بيانات ديناميكية."
                    sx={{ fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
                  />
                  <Typography variant="caption" color="textSecondary" mt={1} display="block">
                    يمكنك استخدام HTML و CSS هنا. استخدم المتغيرات أعلاه لإدراج بيانات ديناميكية.
                  </Typography>
                </Box>
              )}

              {/* Columns Editor */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>اسحب لترتيب الأعمدة، أو قم بإخفائها وتغيير مسمياتها:</Typography>
                  {layout.columns.map((col, index) => (
                    <Paper 
                      key={col.key} 
                      variant="outlined" 
                      draggable
                      onDragStart={(e) => handleColumnDragStart(e, index)}
                      onDragOver={(e) => handleColumnDragOver(e, index)}
                      onDrop={(e) => handleColumnDrop(e, index)}
                      sx={{ 
                        p: 1.5, 
                        mb: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        bgcolor: draggedColIndex === index ? '#f5f5f5' : 'white',
                        borderLeft: col.visible ? '4px solid #4caf50' : '4px solid #bdbdbd'
                      }}
                    >
                      <DragHandleIcon color="action" sx={{ cursor: 'move' }} />
                      
                      <Tooltip title={col.visible ? "إخفاء" : "إظهار"}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleColumnChange(index, 'visible', !col.visible)}
                          color={col.visible ? "primary" : "default"}
                        >
                          {col.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </Tooltip>

                      <Box flexGrow={1}>
                        <TextField
                          label="العنوان (عربي)"
                          size="small"
                          fullWidth
                          value={col.labelAr}
                          onChange={(e) => handleColumnChange(index, 'labelAr', e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="العنوان (En)"
                          size="small"
                          fullWidth
                          value={col.labelEn}
                          onChange={(e) => handleColumnChange(index, 'labelEn', e.target.value)}
                        />
                      </Box>
                      
                      <Box width={80}>
                         <TextField
                          label="العرض"
                          size="small"
                          value={col.width}
                          onChange={(e) => handleColumnChange(index, 'width', e.target.value)}
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Footer Editor */}
              {tabValue === 2 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold">المتغيرات المتاحة (اضغط للنسخ):</Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<ImageIcon />}
                    >
                      إدراج صورة
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'footerHtml')}
                      />
                    </Button>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                    {availableVariables.map((v) => (
                      <Chip 
                        key={v.key} 
                        label={`${v.label} ${v.key}`} 
                        onClick={() => insertVariable(v.key)} 
                        size="small" 
                        variant="outlined" 
                        clickable
                        icon={<CopyIcon />}
                      />
                    ))}
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    value={layout.footerHtml}
                    onChange={(e) => setLayout({ ...layout, footerHtml: e.target.value })}
                    placeholder="<div>...</div>"
                    helperText="أدخل كود HTML للذيل هنا."
                    sx={{ fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
                  />
                </Box>
              )}

              {/* CSS Editor */}
              {tabValue === 3 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>تخصيص CSS (متقدم):</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={20}
                    value={layout.customCss}
                    onChange={(e) => setLayout({ ...layout, customCss: e.target.value })}
                    placeholder=".custom-header { background: #eee; padding: 10px; }"
                    sx={{ fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: Live Preview */}
        <Grid item xs={12} lg={7}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              height: '100%', 
              bgcolor: '#525659', // Dark background like PDF viewers
              overflow: 'auto',
              maxHeight: 'calc(100vh - 200px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}
          >
            <Box sx={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}>
              <Typography variant="h6" color="white" textAlign="center" mb={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
                <PreviewIcon /> معاينة حية
              </Typography>
              <InvoiceTemplate data={mockData} layout={layout} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
